import React, { useCallback, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { FiEye, FiEyeOff, FiCheckCircle } from 'react-icons/fi';
import { useMutation } from '@tanstack/react-query';
import clsx from 'classnames';

import { useAuth } from 'context/AuthContext';
import servicePeople from 'services/people';
import helpers from 'utils/helpers';
import { useCustomPersonDataKeys } from 'context/CustomPersonDataKeysContext';
import styles from 'styles/ProtectedField.module.css';
import Badge from 'components/Badge';
import { COL_OPT_IN, DATE_DISPLAY_FORMAT } from 'utils/constants';
import { formatDate } from 'utils/formatDate';
import { formatRelativeDate } from 'utils/formatRelativeDate';

/**
 * Renders a date value with a hover tooltip showing complementary date information.
 * Uses a portal to render the tooltip on document.body, avoiding overflow clipping from parent containers.
 *
 * @param {Object} props
 * @param {string} props.visibleText - The primary date text displayed inline.
 * @param {string} props.tooltipText - The complementary date text shown on hover (e.g. relative date for absolute display, or vice versa).
 */
const DateWithTooltip = ({ visibleText, tooltipText }) => {
    const [pos, setPos] = useState(null);
    const ref = useRef(null);

    const onMouseEnter = () => {
        const rect = ref.current.getBoundingClientRect();
        setPos({ top: rect.top - 4, left: rect.left + rect.width / 2 });
    };

    return (
        <span ref={ref} onMouseEnter={onMouseEnter} onMouseLeave={() => setPos(null)}>
            {visibleText}
            {pos &&
                ReactDOM.createPortal(
                    <div
                        className="fixed fs-13 px-3 py-2 bg-white text-primary rounded-lg border-1 border-solid border-transparent shadow-tooltip whitespace-nowrap pointer-events-none z-999"
                        style={{
                            top: pos.top - 10,
                            left: pos.left,
                            transform: 'translate(-50%, -100%)',
                            lineHeight: '20.8px'
                        }}
                    >
                        {tooltipText}
                        <div
                            className="absolute"
                            style={{
                                bottom: '-6px',
                                left: '50%',
                                marginLeft: '-8px',
                                borderLeft: '8px solid transparent',
                                borderRight: '8px solid transparent',
                                borderTop: '6px solid #fff'
                            }}
                        />
                    </div>,
                    document.body
                )}
        </span>
    );
};

/**
 * Converts a given UTC date/time value to a formatted string in a specified timezone.
 * Always shows a tooltip with the opposite format (relative dates get absolute tooltip, and vice versa).
 *
 * @function
 * @param {string|Date|number} value - The date/time value to be rendered.
 * @param {Object} [options] - Configuration options.
 * @param {string} [options.timezone] - The timezone in which the date/time should be localized.
 * @param {string} [options.displayFormat='absolute'] - The display format: 'absolute' or 'relative'.
 * @param {boolean} [options.includeTime=false] - Whether to include time in the absolute date.
 * @param {boolean} [options.onlyTime=false] - Whether to show only the time.
 * @param {boolean} [options.isUtc=false] - When true, parses naive timestamp strings as UTC.
 *                                          Only enable when the caller knows its source emits
 *                                          naive UTC strings (e.g. custom person-data values
 *                                          normalized by the API). See `formatDate` for details.
 * @returns {JSX.Element|null} A span element with tooltip. Returns null if the `value` parameter is falsy.
 */
export const renderDate = (
    value,
    {
        timezone,
        displayFormat = DATE_DISPLAY_FORMAT.ABSOLUTE,
        includeTime = false,
        onlyTime = false,
        isUtc = false
    } = {}
) => {
    if (!value) return null;

    const absoluteDate = formatDate(value, { timezone, includeTime, onlyTime, isUtc });
    const relativeDate = formatRelativeDate(value, timezone);

    const isRelative = displayFormat === DATE_DISPLAY_FORMAT.RELATIVE;
    const visibleText = isRelative ? relativeDate : absoluteDate;
    const tooltipText = isRelative ? absoluteDate : relativeDate;

    return <DateWithTooltip visibleText={visibleText} tooltipText={tooltipText} />;
};

/**
 * Renders the label of a dropdown option based on the provided value.
 *
 * @function
 * @param {string} value - The value to search for within the options array.
 * @param {Array<{ value: string|number, label: string }>} options - An array of option objects.
 * @returns {string|null} - The label of the matched option, or the raw value if no match is found, or null if the value is falsy.
 */
export const renderDropdown = (value, options) => {
    if (!value) return null;

    const label = options.find(option => option.value === value)?.label;

    return label || value;
};

/**
 * Processes and renders a checkbox input value based on the provided options.
 *
 * This function takes an input value and transforms it into a comma-separated
 * string of labels based on matching values in the options list. The input value
 * can be a JSON string, an object, or an array.
 *
 * @param {string|Record<any, string|number>|Array<string|number>} value - The input value for the checkbox rendering logic.
 * @param {Array<{value: string, label: string}>} options - The list of available options used for matching and rendering labels.
 * @returns {string|null|*} A comma-separated string of matched labels if successful;
 *                          `null` if the input value is falsy;
 *                          otherwise, the unmodified input value in case of errors or processing
 *                          edge cases.
 */
export const renderCheckbox = (value, options) => {
    if (!value) return null;

    // Parse JSON string if needed
    if (typeof value === 'string' && helpers.isJsonString(value)) {
        try {
            value = JSON.parse(value);
        } catch (error) {
            helpers.trackError(error);
            return value;
        }
    }

    // Convert object to array if needed
    if (value && typeof value === 'object' && !Array.isArray(value)) {
        try {
            value = Object.values(value);
        } catch (error) {
            helpers.trackError(error);
            return value;
        }
    }

    if (!Array.isArray(value)) {
        return value;
    }

    // Create lookup map once (could be memoized if options don't change frequently)
    const optionsMap = new Map(options.map(option => [option.value, option.label]));

    // Single pass: map values to labels, fall back to raw value for removed options
    const labels = [];
    for (const val of value) {
        const label = optionsMap.get(val);
        labels.push(label != null ? label : val);
    }

    return labels.join(', ');
};

/**
 * Renders a boolean value as a styled badge component.
 *
 * @param {any} value - The input value to be evaluated as boolean.
 * @returns {JSX.Element|null} A badge component with the result of the boolean evaluation or `null` if the value is invalid.
 */
export const renderBoolean = value => {
    if (typeof value === 'undefined' || value === '') return null;

    const isTrue = Number(value) > 0;

    return (
        <Badge type={isTrue ? 'success' : 'danger'} css={{ width: 100 }}>
            {isTrue ? 'True' : 'False'}
        </Badge>
    );
};

/**
 * Renders a badge component based on the provided value, indicating the opt-in status.
 *
 * @param {any} value - The value to determine the opt-in status and corresponding badge type.
 * @param {import('services/services').Account} account - The account object containing the opt-in status.
 * @param {import('services/services').Person} person - The person object containing the opt-in confirmation status.
 * @returns {JSX.Element|null} A JSX badge element indicating the status, or `null` if the value is undefined or empty.
 */
export const renderOptIn = (value, account, person) => {
    if (!account.settings.is_double_opt_in_enabled) return renderBoolean(value);

    if (typeof value === 'undefined' || value === '') return null;

    const isTrue = Number(value) > 0;

    return (
        <>
            <Badge type={isTrue ? 'success' : 'danger'} css={{ width: 100 }}>
                {isTrue ? 'True' : 'False'}
            </Badge>
            <Badge className="ml-1 !p-1.5" type={person.is_opt_in_confirmed ? 'success' : 'danger'}>
                <FiCheckCircle className="!m-0" />
            </Badge>
        </>
    );
};

/**
 * @typedef {Object} UseProtectedCompactRenderFunctionSettings
 * @property {boolean} [useOptIn=false] - A flag indicating whether to use the opt-in rendering logic.
 */

/**
 * A function that returns a rendering function with protected and compact rendering logic.
 *
 * The returned function renders values based on the provided data type and definition,
 * supporting various types such as date, dropdown, checkbox, and boolean. It ensures that
 * the rendering is tailored according to the authenticated user's preferences, such as timezone.
 *
 * @returns {(value: any, personDataKey: import('services/services').CustomDataColumn, person: Object, settings?: UseProtectedCompactRenderFunctionSettings) => any} A rendering function that takes a value and an object describing the data key type and definition, and renders the value accordingly.
 */
export const useProtectedCompactRender = () => {
    const auth = useAuth();

    return (value, personDataKey, person, { useOptIn = false } = {}) => {
        // Handle undefined personDataKey (e.g. when we create a new panel property)
        if (!personDataKey) return value;

        if (personDataKey.type === 'date') {
            const displayFormat = personDataKey.config?.date_display_format || DATE_DISPLAY_FORMAT.ABSOLUTE;
            return renderDate(value, { timezone: auth.user.timezone, displayFormat, isUtc: true });
        }

        if (personDataKey.type === 'dropdown') return renderDropdown(value, personDataKey.definition);

        if (personDataKey.type === 'checkbox') return renderCheckbox(value, personDataKey.definition);

        if (useOptIn && personDataKey.title === COL_OPT_IN) return renderOptIn(value, auth.account, person);

        if (personDataKey.type === 'boolean') return renderBoolean(value);

        return value;
    };
};

/**
 * Higher-order function that wraps a given React component to inject a `compactRender`
 * property into it. The `compactRender` property is derived from the custom hook
 * `useProtectedCompactRender`, enabling the wrapped component to utilize this functionality.
 *
 * @param {React.Component} Component - The React component to be wrapped.
 * @returns {React.FunctionComponent} - A new functional component that renders the original component with the `compactRender` property added.
 */
export const withProtectedCompactRender = Component =>
    /**
     * A higher-order component that injects the `compactRender` prop into the wrapped component.
     *
     * @param {Object} props - The props passed to the component.
     * @returns {React.ReactNode} A React component with the `compactRender` prop.
     */
    props => {
        const compactRender = useProtectedCompactRender();

        return <Component {...props} compactRender={compactRender} />;
    };

/**
 * @typedef {Object} ProtectedFieldCompositeClassnamesProp
 * @property {string} [root] - The root class name for the component.
 * @property {string} [composite] - The composite class name for the component.
 * @property {ProtectedFieldClassnamesProp} [field] - The field class name for the component.
 */

/**
 * @typedef {Object} ProtectedFieldCompositeProps
 * @property {Object} person - The person to whom the field is associated.
 * @property {boolean} [hideable] - A flag indicating whether the field can be hidden by user interaction.
 * @property {ProtectedFieldCompositeClassnamesProp} [classNames] - Optional class names for styling the component and its elements.
 * @property {React.ReactNode} [empty] - The value to display when the field is empty.
 * @property {Array<Omit<ProtectedFieldProps, 'classNames'>>} fields - An array of fields to be rendered within the composite.
 */

/**
 * A React component that renders a composite of protected fields with optional masking, unmasking, and customization behavior.
 *
 * @param {ProtectedFieldCompositeProps} props
 * @returns {React.ReactNode}
 */
export function ProtectedFieldComposite({ person, fields, empty, hideable, classNames }) {
    const [values, setValues] = useState(
        fields.reduce(
            (acc, field) => ({
                ...acc,
                [field.dataKey]: {
                    value: null,
                    isHidden: true,
                    isPii: true
                }
            }),
            {}
        )
    );
    const isEmpty = useMemo(() => {
        return Object.values(values).every(value => !value.isHidden && !value.value);
    }, [values]);
    const isPii = useMemo(() => Object.values(values).some(value => value.isPii), [values]);

    const renderField = useCallback((field, value, personDataKey) => {
        setValues(prevValues => {
            const current = prevValues[field.dataKey];

            // Update the value if it's different or if it's hidden
            // It prevents the component from re-rendering when the value is the same (infinity loop)
            if (current?.value !== value || current?.isHidden !== false) {
                return {
                    ...prevValues,
                    [field.dataKey]: {
                        value: value,
                        isHidden: false,
                        isPii: !!personDataKey?.pii_masked
                    }
                };
            }

            return prevValues;
        });

        return field.render?.(value, personDataKey) || value;
    }, []);

    const hide = () => {
        setValues(
            fields.reduce(
                (acc, field) => ({
                    ...acc,
                    [field.dataKey]: {
                        value: null,
                        isHidden: true,
                        isPii: true
                    }
                }),
                {}
            )
        );
    };

    if (isEmpty) {
        // It should behave like a regular ProtectedField
        return (
            <span
                className={clsx(styles.content, classNames?.root, classNames?.field?.root, classNames?.field?.content)}
            >
                {empty}
                {hideable && isPii && (
                    <button className={clsx(styles.unmask, classNames?.field?.unmask)} type="button" onClick={hide}>
                        <FiEyeOff />
                    </button>
                )}
            </span>
        );
    }

    return (
        <span className={clsx('flex items-center gap-2', classNames?.root, classNames?.composite)}>
            {fields.map(field => (
                <ProtectedField
                    render={(value, personDataKey) => renderField(field, value, personDataKey)}
                    person={person}
                    dataKey={field.dataKey}
                    onDiscover={field.onDiscover}
                    onHide={() => {
                        setValues(prevValues => ({
                            ...prevValues,
                            [field.dataKey]: {
                                value: null,
                                isHidden: true
                            }
                        }));

                        return field?.onHide?.();
                    }}
                    hideable={field.hideable}
                    classNames={classNames?.field}
                    labels={field.labels}
                    empty={field.empty}
                    key={field.dataKey}
                    renderLayout={field.renderLayout}
                />
            ))}
        </span>
    );
}

/**
 * @typedef {Object} ProtectedFieldClassnamesProp
 * @property {string} [root] - The root class name for the component.
 * @property {string} [content] - The content class name for the component.
 * @property {string} [mask] - The mask class name for the component.
 * @property {string} [unmask] - The unmask class name for the component.
 */

/**
 * @typedef {Object} ProtectedFieldLabelsProp
 * @property {string} [unmask] - The label for the unmask button.
 */

/**
 * @typedef {Object} ProtectedFieldAdapter
 * @property {any} value - The current display value.
 * @property {boolean} isPii - Whether this field contains PII data.
 * @property {any[]} mutationKey - Unique key for the useMutation hook.
 * @property {() => Promise<any>} mutationFn - Function to fetch the unmasked value.
 */

/**
 * Builds the default adapter for person data fields.
 * Resolves the personDataKey from the custom data keys context, reads the value
 * from person.custom_data_values2, and uses getMaskedValue to unmask.
 *
 * @param {Object} person - The person object.
 * @param {string} dataKey - The title of the data key.
 * @param {import('services/services').CustomDataColumn|null} personDataKey - The resolved person data key.
 * @returns {ProtectedFieldAdapter}
 */
const buildPersonFieldAdapter = (person, dataKey, personDataKey) => ({
    value: personDataKey ? person.custom_data_values2[personDataKey.id] : null,
    isPii: !!personDataKey?.pii_masked,
    mutationKey: ['pii', person.id, dataKey],
    mutationFn: () => {
        if (!personDataKey.pii_masked) return person.custom_data_values2[personDataKey.id];

        return servicePeople.getMaskedValue(person.id, personDataKey.id).then(data => data.value);
    }
});

/**
 * @typedef {Object} ProtectedFieldProps
 * @property {ProtectedFieldAdapter} [adapter] - Custom adapter providing value, isPii, and mutation config.
 *   When provided, `person` and `dataKey` are not required.
 * @property {Object} [person] - The person to whom the field is associated (required without adapter).
 * @property {string} [dataKey] - The title of the data key (required without adapter).
 * @property {(value: any, personDataKey: import('services/services').CustomDataColumn) => any} [render] - A function to render the value of the field.
 * @property {() => void} [onDiscover] - A function to be called when the field is discovered.
 * @property {() => void} [onHide] - A function to be called when the field is hidden.
 * @property {boolean} [hideable] - A flag indicating whether the field can be hidden by user interaction.
 * @property {ProtectedFieldClassnamesProp} [classNames] - Optional class names for styling the component and its elements.
 * @property {ProtectedFieldLabelsProp} [labels] - Optional labels for the component.
 * @property {React.ReactNode} [empty=null] - The value to display when the field is empty.
 * @property {(node: React.ReactNode) => React.ReactNode} [renderLayout] - A function to render the layout of the field.
 */

/**
 * A React component that renders a protected field with optional masking, unmasking, and customization behavior.
 * The component is primarily used to control access to sensitive data based on user roles and permissions.
 *
 * Supports two modes:
 * - **Default mode**: Pass `person` and `dataKey` — the component builds an adapter internally that resolves
 *   the personDataKey from context, reads value from `person.custom_data_values2`, and uses `getMaskedValue`.
 * - **Adapter mode**: Pass an `adapter` object with `{ value, isPii, mutationKey, mutationFn }` — the component
 *   uses the adapter directly. Useful for contexts like audit trail where person data isn't available.
 *
 * @param {ProtectedFieldProps} props
 * @return {React.ReactNode}
 */
export function ProtectedField({
    adapter,
    person,
    dataKey,
    render = value => value,
    onDiscover,
    onHide,
    hideable,
    classNames,
    labels,
    empty = null,
    renderLayout
}) {
    const auth = useAuth();
    const isAdmin = auth.account_role.type === 'admin';
    const customPersonDataKeys = useCustomPersonDataKeys();

    const personDataKey = useMemo(() => {
        if (!customPersonDataKeys || !dataKey) return null;

        return helpers.getPersonDataKeyByTitle(customPersonDataKeys, dataKey);
    }, [customPersonDataKeys, dataKey]);

    const resolvedAdapter =
        adapter || (person && personDataKey ? buildPersonFieldAdapter(person, dataKey, personDataKey) : null);

    const { data, mutate, reset } = useMutation({
        mutationKey: resolvedAdapter ? resolvedAdapter.mutationKey : ['pii-noop'],
        mutationFn: resolvedAdapter ? resolvedAdapter.mutationFn : () => Promise.resolve(null)
    });

    const value = data || (resolvedAdapter ? resolvedAdapter.value : null);
    const isPii = resolvedAdapter ? resolvedAdapter.isPii : false;

    const discover = () => {
        if (!isAdmin) return;

        mutate(null, {
            onSuccess: data => {
                onDiscover?.(data);
            }
        });
    };

    const hide = () => {
        if (!isAdmin) return;

        reset();
        onHide?.();
    };

    // Non-admin with PII, or no adapter resolved yet (waiting for context)
    if ((!isAdmin && isPii) || (!adapter && !customPersonDataKeys))
        return <span className={clsx(styles.mask, classNames?.root, classNames?.mask)}>Protected</span>;

    if (data == null && isPii)
        return (
            <button className={clsx(styles.mask, classNames?.root, classNames?.mask)} type="button" onClick={discover}>
                <FiEye /> {labels?.unmask || 'Unmask'}
            </button>
        );

    const content = render(value, personDataKey, person);
    let node = value ? content : empty;

    if (renderLayout) {
        node = renderLayout(node);
    }

    return (
        <span className={clsx(styles.content, classNames?.root, classNames?.content)}>
            {node}
            {isPii && hideable && (
                <button className={clsx(styles.unmask, classNames?.unmask)} type="button" onClick={hide}>
                    <FiEyeOff />
                </button>
            )}
        </span>
    );
}
